import { useMutation } from "@apollo/client";
import { useCallback } from "react";
import {
  KpiSummaryPeriod,
  SaveUserUiPreferenceMutationMutation,
  SaveUserUiPreferenceMutationMutationVariables,
} from "types/graphql-schema";
import { DateRangeEnum } from "types/topicflow";

import { AssessmentSidebarTab } from "@apps/assessments/components/assessment/types";
import { AllDateRangeEnum } from "@apps/goal-alignment/use-fetch-alignment-goals";
import { ProgramActionToDoStateFilter } from "@apps/programs/components/compliance-program-constants";
import { currentUserVar } from "@cache/cache";

import saveUserUiPreferenceMutation from "./graphql/save-user-ui-preference-mutation";

export enum GoalOverviewGoalRoleFilterEnum {
  both = "both",
  owners = "owning",
  contributors = "contributing",
}

export enum GoalOverviewGoalDisplayEnum {
  compact = "compact",
  details = "details",
}

export enum MeetingsViewEnum {
  overview = "overview",
  calendar = "calendar",
}

export enum CalendarViewEnum {
  week = "week",
  month = "month",
}

export enum MeetingSummaryLengthButtonToggle {
  short = "Short",
  medium = "Medium",
  long = "Long",
}

export type UiPreferenceCache = {
  dashboardRequirementsExpanded: boolean;
  dashboardInsightsExpanded: boolean;
  dashboardRequestsExpanded: boolean;

  // ask ai
  askAiSidebarExpanded: boolean;
  askAiCustomPrompts: string[];

  // dashboard
  dashboardSidebarExpanded: boolean;
  dashboardExpanded: boolean;
  dashboardFeedbackReceivedExpanded: boolean;
  dashboardFeedbackGivenExpanded: boolean;
  dashboardRecognitionReceivedExpanded: boolean;
  dashboardRecognitionGivenExpanded: boolean;
  dashboardPeopleTableManagersExpanded: boolean;
  dashboardPeopleTableReportsExpanded: boolean;
  dashboardPeopleTableDirectTeamExpanded: boolean;
  dashboardPeopleTableFavouritesExpanded: boolean;
  dashboardPersonalGoalsExpanded: boolean;
  dashboardTeamGoalsExpanded: boolean;
  dashboardOrganizationGoalsExpanded: boolean;
  dashboardActionItemsExpanded: [number] | null;
  dashboardRequirementsContainerExpanded: boolean;
  dashboardConnectionsContainerExpanded: boolean;
  dashboardSidebarActionItemContainerExpanded: boolean;
  dashboardSidebarGoalContainerExpanded: boolean;
  dashboardSidebarRecognitionContainerExpanded: boolean;
  dashboardSidebarFeedbackContainerExpanded: boolean;

  // Artifact meeting collapsible state
  meetingActionItemsExpanded: any;
  oneononeActionItemsExpanded: any;
  meetingOpenActionItemsExpanded: boolean;
  meetingClosedActionItemsExpanded: boolean;
  meetingOpenGoalsExpanded: boolean;
  meetingClosedGoalsExpanded: boolean;
  meetingDraftGoalsExpanded: boolean;
  meetingReceivedRecognitionsExpanded: boolean;
  meetingGivenRecognitionsExpanded: boolean;
  meetingReceivedFeedbackExpanded: boolean;
  meetingGivenFeedbackExpanded: boolean;

  // Meeting container
  meetingSidebarActionItemContainerExpanded: boolean;
  meetingSidebarRelatedRequirementsContainerExpanded: boolean;
  meetingSidebarGoalsContainerExpanded: boolean;
  meetingSidebarRecognitionsContainerExpanded: boolean;
  meetingSidebarFeedbackContainerExpanded: boolean;
  meetingSidebarRecentlyCompletedAssessmentsContainerExpanded: boolean;
  meetingSidebarActionItemContainerToggled: boolean;
  meetingSidebarRelatedRequirementsContainerToggled: boolean;
  meetingSidebarGoalsContainerToggled: boolean;
  meetingSidebarRecognitionsContainerToggled: boolean;
  meetingSidebarFeedbackContainerToggled: boolean;
  meetingSidebarRecentlyCompletedAssessmentsContainerToggled: boolean;

  // Artifact meeting sidebar filter
  meetingActionItemsJustMe: boolean;
  oneononeActionItemsJustMe: boolean;
  meetingActionItemsJustMeeting: boolean;
  oneononeActionItemsJustMeeting: boolean;
  meetingGoalsJustMe: boolean;
  oneononeGoalsJustMe: boolean;
  meetingGoalsJustMeeting: boolean;
  oneononeGoalsJustMeeting: boolean;
  meetingRecognitionsJustMe: boolean;
  oneononeRecognitionsJustMe: boolean;
  oneononeRecognitionsJustMeeting: boolean;
  meetingRecognitionsJustMeeting: boolean;
  oneononeFeedbackOnlyParticipants: boolean;
  oneononePerformanceAssessments: boolean;
  oneononePeerAssessments: boolean;
  oneononeManagerAssessments: boolean;
  meetingSummaryLength: MeetingSummaryLengthButtonToggle;

  // Meetings/calendars
  calendarFilters: any;
  meetingsView: MeetingsViewEnum;
  meetingSidebarExpanded: boolean;
  calendarView: CalendarViewEnum;
  meetingOverviewShowWeekends: boolean;

  toggledTemplateGroups: any;
  meetingInsightsExpanded: any;
  explorerCollapsedGroupIds: any;
  kpiSummaryTableCollapsedIds: any;
  kpiListSummaryPeriod: KpiSummaryPeriod;

  objectiveAlignmentExpandedIds: number[];
  objectiveAlignmentIsShowingKeyResults: boolean;
  objectiveAlignmentIsShowingUnaligned: boolean;
  objectiveAlignmentIsShowingClosed: boolean;
  goalOverviewGoalDisplay: GoalOverviewGoalDisplayEnum;
  goalOverviewGoalRoleFilter: GoalOverviewGoalRoleFilterEnum;

  programsShowOnlyMyProgramsFlag: boolean;
  programsDateRangeDefault: DateRangeEnum | AllDateRangeEnum;
  programReportingDetailsExpanded: boolean;

  assessmentSidebarExpanded: boolean;
  assessmentDeliverySidebarExpanded: boolean;
  assessmentOwnedGoalsExpanded: boolean;
  assessmentContributingGoalsExpanded: boolean;
  assessmentSidebarSelectedTab: null | AssessmentSidebarTab;

  programActionsFilter: ProgramActionToDoStateFilter;

  leftNavigationSidebarExpanded: boolean;
  leftNavigationDirectReportsListExpanded: boolean;
  leftNavigationPerformanceExpanded: boolean;
  leftNavigationMeetingsExpanded: boolean;
  leftNavigationMeetingsFullExpanded: boolean;

  orgChartShowInactiveUsers: boolean;

  coachingInsightsExpanded: boolean;

  // Profile
  profileSidebarExpanded: boolean;
  profileWorkHistoryRange: "week" | "month";
  privateNotesCalloutAcknowledged: boolean;
};

export const formatUiPreferenceCache = (
  uiPreferenceCache: UiPreferenceCache,
  update: Partial<UiPreferenceCache>,
): UiPreferenceCache => {
  const defaultUiPreferenceCache = {
    // meetings page
    calendarFilters: [],
    meetingsView: MeetingsViewEnum.calendar,
    meetingSidebarExpanded: true,
    calendarView: CalendarViewEnum.week,

    // dashboard
    dashboardRequirementsExpanded: true,
    dashboardInsightsExpanded: true,
    dashboardRequestsExpanded: true,

    dashboardOrganizationGoalsExpanded: true,
    dashboardRequirementsContainerExpanded: true,
    dashboardConnectionsContainerExpanded: true,
    dashboardSidebarExpanded: true,
    dashboardActionItemsExpanded: null,
    dashboardFeedbackReceivedExpanded: false,
    dashboardFeedbackGivenExpanded: false,
    dashboardRecognitionReceivedExpanded: true,
    dashboardRecognitionGivenExpanded: true,
    dashboardPeopleTableManagersExpanded: true,
    dashboardPeopleTableReportsExpanded: true,
    dashboardPeopleTableDirectTeamExpanded: true,
    dashboardPeopleTableFavouritesExpanded: true,
    dashboardExpanded: false,
    dashboardPersonalGoalsExpanded: true,
    dashboardTeamGoalsExpanded: true,
    dashboardSidebarActionItemContainerExpanded: true,
    dashboardSidebarGoalContainerExpanded: true,
    dashboardSidebarRecognitionContainerExpanded: true,
    dashboardSidebarFeedbackContainerExpanded: true,

    // goalOverview
    goalOverviewGoalDisplay: GoalOverviewGoalDisplayEnum.compact,
    goalOverviewGoalRoleFilter: GoalOverviewGoalRoleFilterEnum.both,

    // explorer
    explorerCollapsedGroupIds: [],

    // org chart
    orgChartShowInactiveUsers: false,

    // kpis
    kpiListSummaryPeriod: KpiSummaryPeriod.Monthly,
    kpiSummaryTableCollapsedIds: [],

    // meetings
    meetingActionItemsExpanded: null,
    meetingClosedActionItemsExpanded: true,
    meetingClosedGoalsExpanded: true,
    meetingDraftGoalsExpanded: true,
    meetingInsightsExpanded: true,
    meetingOpenActionItemsExpanded: true,
    meetingOpenGoalsExpanded: true,
    oneononeActionItemsExpanded: false,
    toggledTemplateGroups: [],
    meetingReceivedRecognitionsExpanded: true,
    meetingGivenRecognitionsExpanded: false,
    meetingReceivedFeedbackExpanded: false,
    meetingGivenFeedbackExpanded: false,
    meetingSummaryLength: MeetingSummaryLengthButtonToggle.short,
    meetingOverviewShowWeekends: false,

    // Meeting container
    meetingSidebarActionItemContainerExpanded: true,
    meetingSidebarRelatedRequirementsContainerExpanded: true,
    meetingSidebarGoalsContainerExpanded: true,
    meetingSidebarRecognitionsContainerExpanded: true,
    meetingSidebarFeedbackContainerExpanded: true,
    meetingSidebarRecentlyCompletedAssessmentsContainerExpanded: true,
    meetingSidebarActionItemContainerToggled: false,
    meetingSidebarRelatedRequirementsContainerToggled: false,
    meetingSidebarGoalsContainerToggled: false,
    meetingSidebarRecognitionsContainerToggled: false,
    meetingSidebarFeedbackContainerToggled: false,
    meetingSidebarRecentlyCompletedAssessmentsContainerToggled: false,

    // meeting filter context
    oneononeActionItemsJustMeeting: true,
    meetingActionItemsJustMeeting: true,
    oneononeActionItemsJustMe: false,
    meetingActionItemsJustMe: false,
    meetingGoalsJustMe: false,
    oneononeGoalsJustMe: false,
    oneononeGoalsJustMeeting: true,
    meetingGoalsJustMeeting: true,
    meetingRecognitionsJustMe: false,
    oneononeRecognitionsJustMe: false,
    oneononeRecognitionsJustMeeting: true,
    meetingRecognitionsJustMeeting: true,
    oneononeFeedbackOnlyParticipants: false,
    oneononePerformanceAssessments: true,
    oneononePeerAssessments: false,
    oneononeManagerAssessments: false,

    // obejctiveAlignment
    objectiveAlignmentExpandedIds: [],
    objectiveAlignmentIsShowingKeyResults: false,
    objectiveAlignmentIsShowingUnaligned: false,
    objectiveAlignmentIsShowingClosed: false,

    // programs
    programsShowOnlyMyProgramsFlag: false,
    programsDateRangeDefault: DateRangeEnum.thisQuarter,
    programReportingDetailsExpanded: false,

    // assessments
    assessmentSidebarExpanded: true,
    assessmentDeliverySidebarExpanded: true,
    assessmentOwnedGoalsExpanded: true,
    assessmentContributingGoalsExpanded: true,
    assessmentSidebarSelectedTab: null,

    // programs
    programActionsFilter: ProgramActionToDoStateFilter.All,

    // layout
    leftNavigationSidebarExpanded: true,
    leftNavigationDirectReportsListExpanded: true,
    leftNavigationPerformanceExpanded: true,
    leftNavigationMeetingsExpanded: true,
    leftNavigationMeetingsFullExpanded: false,

    // general
    coachingInsightsExpanded: false,
    askAiSidebarExpanded: false,
    askAiCustomPrompts: [],

    // profile
    profileSidebarExpanded: true,
    profileWorkHistoryRange: "week",
    privateNotesCalloutAcknowledged: false,
  };
  return {
    ...defaultUiPreferenceCache,
    ...uiPreferenceCache,
    ...update,
  };
};

export const getUiPreferenceCache = (): UiPreferenceCache => {
  const currentUser = currentUserVar();
  const uiPreferenceCache = formatUiPreferenceCache(
    JSON.parse(currentUser.uiPreferenceCache),
    {},
  );
  return uiPreferenceCache;
};

const useUiPreferenceCache = () => {
  const currentUser = currentUserVar();
  const uiPreferenceCache = getUiPreferenceCache();
  const [saveUiUserPrefs] = useMutation<
    SaveUserUiPreferenceMutationMutation,
    SaveUserUiPreferenceMutationMutationVariables
  >(saveUserUiPreferenceMutation);

  const saveUiPreference = useCallback(
    (config: Partial<UiPreferenceCache>) => {
      const newUiPreferenceCache = JSON.stringify(
        formatUiPreferenceCache(uiPreferenceCache, config),
      );
      saveUiUserPrefs({
        variables: {
          userId: currentUser.id,
          uiPreferenceCache: newUiPreferenceCache,
        },

        optimisticResponse: {
          updateUser: {
            user: {
              ...currentUser,
              uiPreferenceCache: newUiPreferenceCache,
            },
            __typename: "UpdateUserMutation",
          },
          __typename: "Mutation",
        },
      });
    },
    [uiPreferenceCache, currentUser, saveUiUserPrefs],
  );

  return { uiPreferenceCache, saveUiPreference };
};

export default useUiPreferenceCache;
